import React, { useMemo, useRef, useState, useEffect } from 'react'
import { CampaignDetailResponse, FormField } from '../core/_models'
import {
  importLeads, getSubmissionsByCampaign, saveOutboundCall, triggerCallInitiate,
  getWelcomeStatusCall, getWelcomeResultCall, getWelcomeStatus, getWelcomeProductKnowledge,
  deleteDataSubmission, updateLeadStatus
} from '../core/_requests'
import Swal from 'sweetalert2'
import * as XLSX from 'xlsx'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { Trash2 } from 'lucide-react'
import { useMutation } from 'react-query'
import { useAuth } from '../../../../../modules/auth'

type Props = {
  data: CampaignDetailResponse 
  externalTicketTrigger?: { phone: string, timestamp: number } | null
  onTriggerProcessed?: () => void
}

export const CampaignDetailInfo = ({ data, externalTicketTrigger, onTriggerProcessed }: Props) => {
  
  const { currentUser } = useAuth();
  const userRoles = currentUser?.roles || ["ROLE_AGENT"];
  const isAgent = userRoles.includes('ROLE_AGENT');
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importPreview, setImportPreview] = useState<any[]>([])
  const [realData, setRealData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalData, setTotalData] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const [activeTab, setActiveTab] = useState<'all' | 'close'>('all')

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<any | null>(null)
  const [callNote, setCallNote] = useState('')
  const [policyNumber, setPolicyNumber] = useState('')

  const [welcomeStatusCall, setWelcomeStatusCall] = useState<Array<{ tablecode: string }>>([])
  const [welcomeResultCall, setWelcomeResultCall] = useState<Array<{ tablecode: string }>>([])
  const [welcomeStatus, setWelcomeStatus] = useState<Array<{ tablecode: string }>>([])
  const [welcomeProductKnowledge, setWelcomeProductKnowledge] = useState<Array<{ tablecode: string }>>([])
  
  const [valWelcomeStatusCall, setValWelcomeStatusCall] = useState('')
  const [valWelcomeResultCall, setValWelcomeResultCall] = useState('')
  const [valWelcomeStatus, setValWelcomeStatus] = useState('')
  const [valWelcomeProductKnowledge, setValWelcomeProductKnowledge] = useState('')
  const [isLoadingStats, setIsLoadingStats] = useState(true)
  const [iconColorClass, setIconColorClass] = useState('text-primary')
  
  const sortedFields = useMemo(() => {
    const baseFields: FormField[] = data?.formFields ?? []
    return [...baseFields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  }, [data?.formFields])

  useEffect(() => {
    const hour = new Date().getHours()
    if (hour >= 18 || hour < 6) {
      setIconColorClass('text-warning')
    } else {
      setIconColorClass('text-primary')
    }
  }, [])

  const fetchInitialData = async () => {
    try {
      const [wscRes, wrcRes, wsRes, wpkRes] = await Promise.all([
          getWelcomeStatusCall(),
          getWelcomeResultCall(),
          getWelcomeStatus(),
          getWelcomeProductKnowledge()
      ])
      setWelcomeStatusCall(wscRes.data)
      setWelcomeResultCall(wrcRes.data)
      setWelcomeStatus(wsRes.data)
      setWelcomeProductKnowledge(wpkRes.data)
    } catch {
      toast.error('Failed to fetch initial data')
    } finally {
      setIsLoadingStats(false)
    }
  }

  useEffect(() => {
    if (externalTicketTrigger && realData.length > 0) {
      const matchingLead = realData.find(l => 
        l.phone_number?.includes(externalTicketTrigger.phone) || 
        externalTicketTrigger.phone.includes(l.phone_number)
      )
      if (matchingLead) {
        setSelectedLead(matchingLead)
        setIsDrawerOpen(true)
        if (onTriggerProcessed) onTriggerProcessed()
      }
    }
  }, [externalTicketTrigger, realData, onTriggerProcessed])

  const fetchLeads = async (page: number = 1, limit: number = pageSize) => {
    if (!data.id) return
    setLoading(true)
    try {
      const tabParam = activeTab === 'close' ? '&status=close' : ''
      const response = await getSubmissionsByCampaign(data.id as string, `page=${page}&items_per_page=${limit}${tabParam}`)
      setRealData(response.data || [])
      setTotalData(response.total || 0)
      setCurrentPage(page)
    } catch (e) {
      console.error("Gagal fetch leads:", e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLeads(1, pageSize)
    fetchInitialData()
  }, [data.id, pageSize, activeTab])

  const totalPages = Math.ceil(totalData / pageSize)

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value)
    setPageSize(newSize)
    setCurrentPage(1) 
  }

  const handleExportTemplate = () => {
    if (sortedFields.length === 0) return Swal.fire('Error', 'Field belum dikonfigurasi', 'error')
    const headers = [[...sortedFields.map((f) => f.fieldName || '')]]
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet(headers)
    XLSX.utils.book_append_sheet(wb, ws, 'Template')
    XLSX.writeFile(wb, `Template_Leads_${data.name.replace(/\s+/g, '_')}.xlsx`)
  }

  const handleImportLeads = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (fileInputRef.current) fileInputRef.current.value = ''
    const reader = new FileReader()
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target?.result
        const wb = XLSX.read(bstr, { type: 'binary' })
        const dataJson: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]])
        
        if (dataJson.length === 0) return Swal.fire('Error', 'File kosong', 'error')

        Swal.fire({ title: 'Memeriksa duplikasi...', didOpen: () => Swal.showLoading(), allowOutsideClick: false })
        const existingResponse = await getSubmissionsByCampaign(data.id as string, `all=true`)
        const existingLeads = existingResponse?.data || [];
        const excelFields = dataJson.length > 0 ? Object.keys(dataJson[0]) : [];

        const createFingerprint = (obj: any, fieldsToFields: string[]) => {
          return JSON.stringify(
            fieldsToFields
              .sort() 
              .reduce((acc: any, key) => {
                const val = obj[key] !== null && obj[key] !== undefined ? obj[key] : '';
                acc[key] = String(val).trim().toLowerCase();
                return acc;
              }, {})
          );
        };

        const existingFingerprints = new Set(
          existingLeads.map((item: any) => createFingerprint(item, excelFields))
        );

        const uniqueData = dataJson.filter(newItem => {
          const newFingerprint = createFingerprint(newItem, excelFields);
          return !existingFingerprints.has(newFingerprint);
        });

        const duplicateCount = dataJson.length - uniqueData.length
        if (uniqueData.length === 0) {
          return Swal.fire('Info', 'Semua data di file sudah ada di database (Duplikat).', 'info')
        }

        setImportPreview(uniqueData.slice(0, 5))
        
        Swal.fire({
          title: 'Pratinjau Berhasil',
          html: `Total data: <b>${dataJson.length}</b><br/>` +
                `Data baru: <b style="color: green">${uniqueData.length}</b><br/>` +
                `Data duplikat diabaikan: <b style="color: red">${duplicateCount}</b><br/><br/>` +
                `Lanjutkan upload data baru?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Ya, Upload'
        }).then((res) => {
          if (res.isConfirmed) {
            handleConfirmUpload(uniqueData)
          } else {
            setImportPreview([])
            if (fileInputRef.current) fileInputRef.current.value = ''
          }
        })

      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Gagal memproses data', 'error')
      }
    }
    reader.readAsBinaryString(file)
  }

  const deleteSubmission = useMutation((id: any) => deleteDataSubmission(id), {
    onSuccess: () => {
      toast.success('Data deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete data')
    }
  })

  const handleDelete = async (id: any) => {
    await deleteSubmission.mutateAsync(id)
  }

  const handleConfirmUpload = async (allData: any[]) => {
    Swal.fire({ title: 'Uploading...', didOpen: () => Swal.showLoading() })
    try {
      await importLeads(data.id as string, allData)
      Swal.fire('Berhasil', 'Data masuk antrian.', 'success')
      setImportPreview([])
      if (fileInputRef.current) fileInputRef.current.value = ''
      fetchLeads(1)
    } catch (error: any) { Swal.fire('Gagal', 'Error server', 'error') }
  }

  const openCallDrawer = async (lead: any, fieldName: string) => {
    if (lead.call_count >= 3) {
      Swal.fire({
        title: 'Batas Panggilan Tercapai',
        text: 'Lead ini sudah dihubungi sebanyak 3 kali. Tidak diperbolehkan melakukan panggilan keempat.',
        icon: 'warning',
        confirmButtonColor: '#6366f1'
      })
      return
    }

    setSelectedLead(lead)
    setCallNote('')
    setPolicyNumber('')
    Swal.fire({
      title: 'Connecting...',
      text: `Menghubungi ${lead.phone_number}`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    })
    try {
      await triggerCallInitiate(data.id as string, lead.id, fieldName)
      Swal.close()
      setIsDrawerOpen(true) 
    } catch (e: any) { 
      Swal.fire('Gagal Call', e.response?.data?.message || 'Pastikan Extension Anda Aktif', 'error')
    }
  }

  const callClosed = async (lead: any, fieldName: string) => {
    setSelectedLead(lead)
    try {
      await triggerCallInitiate(data.id as string, lead.id, fieldName)
      Swal.close()
      // setIsDrawerOpen(true) 
    } catch (e: any) { 
      Swal.fire('Gagal Call', e.response?.data?.message || 'Pastikan Extension Anda Aktif', 'error')
    }
  }

  const handleSaveCallResult = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedLead?.id) return
    setLoading(true)
    try {
      await saveOutboundCall({
        campaignId: data.id as string,
        submissionId: selectedLead.id,
        welcomeStatusCall: valWelcomeStatusCall,
        welcomeResultCall: valWelcomeResultCall,
        welcomeStatus: valWelcomeStatus,
        productKnowledge: valWelcomeProductKnowledge,
        notes: callNote
      })
      setValWelcomeStatusCall('')
      setValWelcomeResultCall('')
      setValWelcomeStatus('')
      setValWelcomeProductKnowledge('')
      setIsDrawerOpen(false)
      fetchLeads(currentPage) 
    } catch (err: any) {
        Swal.fire('Gagal', err || 'Gagal simpan data', 'error')
    } finally {
      setLoading(false)
    }
  }

  const displayData = useMemo(() => {
    if (importPreview.length > 0) return importPreview
    return realData
  }, [importPreview, realData])

  const extractPolicyNumber = (leadObj: any) => {
    if (!leadObj) return '';
    if (leadObj.SubmissionValues && typeof leadObj.SubmissionValues === 'string') {
      try {
        const parsed = JSON.parse(leadObj.SubmissionValues);
        const possibleJsonKeys = [
          'nopolis', 'nopolicy', 'policyno', 'policynumber', 
          'nomorpolis', 'insurancenumber', 'insuranceno', 
          'nopol', 'nop', 'nopoli', 'polisno', 'policynum'
        ];
        const foundJsonKey = Object.keys(parsed).find(key => {
          const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
          return possibleJsonKeys.includes(normalized);
        });
        if (foundJsonKey && parsed[foundJsonKey]) return String(parsed[foundJsonKey]);
      } catch (e) {}
    }
    const possibleRootKeys = [
      'nopolis', 'nopolicy', 'policyno', 'policynumber', 
      'nomorpolis', 'insurancenumber', 'insuranceno', 
      'nopol', 'nop', 'nopoli', 'polisno', 'policynum'
    ];
    const foundRootKey = Object.keys(leadObj).find(key => {
      const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
      return possibleRootKeys.includes(normalized);
    });
    return foundRootKey ? String(leadObj[foundRootKey]) : '';
  };

  useEffect(() => {
    if (selectedLead) {
      setPolicyNumber(extractPolicyNumber(selectedLead));
    } else {
      setPolicyNumber('');
    }
  }, [selectedLead]);

  return (
    <div className='d-flex flex-column gap-7 animate__animated animate__fadeIn'>
      
      {/* SCRIPT CARD */}
      <div className='card border-0 shadow-sm' style={{ borderRadius: '20px', overflow: 'hidden' }}>
        <div className='card-header border-0 py-6' style={{ background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)' }}>
          <h3 className='card-title flex-column'>
            <span className='card-label fw-bold fs-3 text-white'>Campaign Script</span>
            <span className='text-white opacity-75 mt-1 fs-7'>Gunakan narasi ini untuk nasabah</span>
          </h3>
        </div>
        <div className='card-body'>
          <div className='p-8 rounded-4 fs-6 text-gray-700 position-relative' 
               style={{ background: 'var(--bs-gray-100)', border: '1px dashed var(--bs-gray-300)' }}>
               <i className='bi bi-quote fs-1 position-absolute top-0 end-0 m-4 opacity-10'></i>
               <div className='text-gray-800' dangerouslySetInnerHTML={{ __html: data?.script || '<em>No script available.</em>' }} />
          </div>
        </div>
      </div>

      {/* DATABASE CARD */}
      <div className='card border-0 shadow-sm' style={{ borderRadius: '20px' }}>
        
        <div className='card-header border-0 pt-7 px-8 bg-body' style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
          <div className='card-title flex-column'>
            <div className='d-flex align-items-center mb-1'>
              <span className='card-label fw-bold fs-3 text-gray-800 me-2'>
                {importPreview.length > 0 ? 'Review Import' : 'Leads Database'}
              </span>
              {loading && <span className='spinner-border spinner-border-sm text-primary'></span>}
            </div>
            <span className='text-muted fs-7 fw-semibold'>Total {totalData} leads terdaftar.</span>
          </div>
          <div className='card-toolbar gap-3'>
            {!onTriggerProcessed && (
              <>
                <input type='file' ref={fileInputRef} onChange={handleImportLeads} className='d-none' accept='.xlsx,.xls' />
                <button className='btn btn-sm btn-primary fw-bold px-5 border' 
                        style={{ borderRadius: '10px' }} onClick={handleExportTemplate}>
                  <i className="bi bi-download me-2"></i>Template
                </button>
                <button className='btn btn-sm btn-primary fw-bold px-5 shadow-sm' 
                        style={{ borderRadius: '10px' }} onClick={() => fileInputRef.current?.click()}>
                  <i className="bi bi-file-earmark-arrow-up me-2"></i>Import Leads
                </button>
              </>
            )}
          </div>
        </div>

        <div className="px-8 pt-2 d-flex justify-content-between align-items-center flex-wrap gap-4">
          {/* Navigasi Tab */}
          <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold">
            <li className="nav-item">
              <a className={clsx("nav-link text-active-primary cursor-pointer py-3 me-6", activeTab === 'all' && 'active')} 
                 onClick={() => setActiveTab('all')}>
                All Leads
              </a>
            </li>
            <li className="nav-item">
              <a className={clsx("nav-link text-active-primary cursor-pointer py-3", activeTab === 'close' && 'active')} 
                 onClick={() => setActiveTab('close')}>
                Closed Leads 
              </a>
            </li>
          </ul>

          <div className="alert alert-custom alert-light-primary border-0 p-3 mb-0 d-flex align-items-center" style={{ borderRadius: '10px', backgroundColor: '#f0f4ff' }}>
            <i className="bi bi-info-circle-fill text-primary fs-5 me-3"></i>
            <div className="text-primary fs-7 fw-semibold">
              Data dengan status <strong className="text-dark">Close</strong> tetap dapat di-call, namun tidak akan update ke portal.
            </div>
          </div>
        </div>

        <div className="px-8 py-4 bg-light-secondary d-flex flex-wrap align-items-center gap-3">
        
          
        </div>
        
        <div className='card-body p-0'>
          <div className='table-responsive'>
            <table className='table table-row-bordered table-row-gray-200 align-middle gs-9 gy-4 mb-0'>
              <thead>
                <tr className='fw-bold fs-7 text-uppercase text-gray-600 bg-light'>
                  <th className='min-w-100px sticky-column'>STATUS</th>
                  {!isAgent && activeTab === 'all' ? (
                  <th className='min-w-100px sticky-column'>CHANGE STATUS</th>
                  ) : ('')}
                  <th className='min-w-150px'>PANGGILAN KE</th>
                  {sortedFields.map((f, i) => (
                    <th key={i} className='min-w-150px'>
                      <span className="text-gray-500 fs-9 me-1">ℹ</span>{f.fieldName?.toUpperCase()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='fs-6'>
                {displayData.map((row, idx) => (
                  <tr key={idx} className={clsx(
                      importPreview.length > 0 && 'bg-light-success',
                      row.status_submission === 'calling' && 'bg-light-primary pulse pulse-primary'
                    )}>
                    
                    <td className='sticky-column bg-body' style={{ left: 0 }}>
                      {importPreview.length > 0 ? (
                        <span className='badge badge-light-warning'>PREVIEW</span>
                      ) : (
                        <div className='d-flex align-items-center'>
                          
                          {(row.assigned_to === null) && (
                            <button
                              className='btn btn-icon btn-sm btn-light-danger me-2'
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this data?')) {
                                  handleDelete(row.id)
                                }
                              }}
                              title='Delete User'
                              disabled={deleteSubmission.isLoading}
                            >
                              {deleteSubmission.isLoading ? (
                                <span className='spinner-border spinner-border-sm' role='status'></span>
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          )}   

                          
                            <span className={clsx(
                              'badge fw-bold px-4 py-3 text-uppercase',
                              row.status_submission?.toLowerCase() === 'ready' ? 'badge-light-primary' : 
                              row.status_submission?.toLowerCase() === 'calling' ? 'badge-primary' : 
                              row.status_submission?.toLowerCase() === 'close' ? 'badge-light-success' : 'badge-light-warning'
                            )} style={{ borderRadius: '8px', minWidth: '85px', textAlign: 'center' }}>
                              {row.status_submission || 'READY'}
                            </span>

                        </div>
                      )}
                    </td>
                    
                    {!isAgent && activeTab === 'all' ? (
                    <td className='sticky-column bg-body' style={{ left: 0 }}>
                          {row.assigned_to !== null ? (
                              <select
                                className={clsx(
                                  'form-select form-select-sm fw-bold text-uppercase'
                                )}
                                style={{ borderRadius: '8px', width: 'fit-content', cursor: 'pointer', minWidth: '120px' }}
                                value={row.status_submission || ''}
                                onChange={async (e) => {
                                const rawStatus = e.target.value
                                  if (!rawStatus) return

                                  const newStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase()

                                  try {
                                    await updateLeadStatus(row.id, newStatus)
                                    
                                    toast.success(`Status berhasil diubah menjadi ${newStatus.toUpperCase()}`)
                                    
                                    fetchLeads(currentPage) 
                                  } catch (err) {
                                    console.error(err)
                                    toast.error('Gagal mengubah status ke server')
                                  }
                                }}
                              >
                                <option value=''>Pilih Status...</option>
                                {welcomeStatus?.map((opt: any) => (
                                  <option key={opt.tablecode} value={opt.tablecode}>
                                    {opt.tablecode?.toUpperCase()}
                                  </option>
                                ))}
                              </select>
                            ) : ('-')
                          }   

                    </td>
                    ) : ('')}
                    <td className='text-gray-600 fw-medium'>
                      {importPreview.length > 0 ? (
                        <span className='text-muted fs-7'>-</span>
                      ) : (
                        <span className={clsx(
                          'badge fw-bolder px-3 py-2 fs-7',
                          row.call_count === 0 && 'badge-light-success',
                          row.call_count === 1 && 'badge-light-info',
                          row.call_count === 2 && 'badge-light-warning',
                          row.call_count >= 3 && 'badge-light-danger text-danger'
                        )} style={{ borderRadius: '6px' }} title={`Sudah ditelepon ${row.call_count} kali`}>
                          {row.call_count >= 3 ? '3/3 (Max)' : `${row.call_count}/3`}
                        </span>
                      )}
                    </td>

                    {sortedFields.map((f: any, i: number) => {
                        const isPhoneField = f.fieldType === 'Phone';
                        const cellValue = row[f.fieldName || ''] || '-';

                        return (
                          <td key={i} className='text-gray-600 fw-medium'>
                            <div className='d-flex align-items-center'>
                              
                              {isPhoneField && f.canCall && cellValue !== '-' && (
                                <button
                                  className={clsx(
                                    'btn btn-icon btn-md me-2',
                                    (row.call_count >= 3) ? row.status_submission === 'Close' ? 'btn-bg-light btn-active-color-primary':'btn-light-secondary opacity-50' : 'btn-bg-light btn-active-color-primary'
                                  )}
                                  style={{ width: '28px', height: '28px', borderRadius: '8px' }}
                                  title={(row.call_count >= 3) ? 'Batas maksimal panggilan tercapai (3x)' : `Call ${cellValue}`}
                                  disabled={(row.call_count >= 3 || importPreview.length > 0) ? row.status_submission === 'Close' ? false:true:false }
                                  onClick={() => {
                                    const tempLead = { ...row };
                                    if (row.status_submission === 'Close') {
                                      callClosed(tempLead, f.fieldName);
                                    } else {
                                      openCallDrawer(tempLead, f.fieldName);
                                    }
                                  }}
                                >
                                  <i className={clsx(
                                    'bi bi-telephone-fill fs-8',
                                    row.call_count >= 3 ? row.status_submission === 'Close' ? 'text-success' : 'text-gray-400' : 'text-success'
                                  )}></i>
                                </button>
                              )}

                              {isPhoneField && !f.canCall && cellValue !== '-' && (
                                <i className='bi bi-telephone-x fs-8 text-gray-400 ms-2 me-2' title="Cannot call this field"></i>
                              )}
                              <span>{cellValue}</span>
                            </div>
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* FOOTER */}
        {!importPreview.length && totalData > 0 && (
          <div className='card-footer py-4 d-flex justify-content-between align-items-center bg-body border-0' style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}>
            <div className='d-flex align-items-center gap-4 text-gray-600 fs-7 fw-bold'>
              <div>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalData)} of {totalData} leads
              </div>
              <div className='d-flex align-items-center gap-1'>
                <span className='text-muted fw-semibold'>Rows:</span>
                <select 
                  className='form-select form-select-solid form-select-sm py-1 px-3 fw-bold' 
                  style={{ width: '75px', borderRadius: '8px', background: 'var(--bs-gray-100)' }}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            
            <ul className='pagination pagination-outline mb-0'>
              <li className={clsx('page-item previous', currentPage === 1 && 'disabled')}>
                <button className='page-link shadow-sm border-0' onClick={() => fetchLeads(currentPage - 1)}>
                  <i className='bi bi-chevron-left fs-7'></i>
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                (i + 1 === 1 || i + 1 === totalPages || Math.abs(i + 1 - currentPage) <= 1) && (
                  <li key={i} className={clsx('page-item mx-1', currentPage === i + 1 && 'active')}>
                    <button className='page-link shadow-sm border-0 fw-bold' style={{ borderRadius: '10px' }} onClick={() => fetchLeads(i + 1)}>{i + 1}</button>
                  </li>
                )
              ))}
              <li className={clsx('page-item next', currentPage === totalPages && 'disabled')}>
                <button className='page-link shadow-sm border-0' onClick={() => fetchLeads(currentPage + 1)}>
                  <i className='bi bi-chevron-right fs-7'></i>
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* DRAWER & OVERLAY */}
      <div className={clsx(
        'bg-body shadow-lg position-fixed top-0 end-0 h-100 z-index-100 d-flex flex-column', 
        isDrawerOpen ? 'animate__animated animate__slideInRight' : 'd-none'
      )} style={{ width: '100%', maxWidth: '550px', zIndex: 9999, borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px' }}>
        <div className='card w-100 h-100 rounded-0 border-0' style={{ borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px' }}>
          <div className='card-header pe-7 flex-grow-0 border-0 pt-8'>
            <div className='card-title'>
              <div className='d-flex align-items-center'>
                <div className='symbol symbol-50px me-4'>
                    <div className='symbol-label fs-2 fw-bold bg-light-primary text-primary'>
                        {selectedLead?.full_name?.charAt(0) || 'C'}
                    </div>
                </div>
                <div className='d-flex flex-column'>
                    <span className='fs-3 fw-bolder text-gray-900'>Call Result</span>
                    <span className='text-muted fw-bold fs-7'>{selectedLead?.full_name}</span>
                </div>
              </div>
            </div>
            <div className='card-toolbar'>
              <button className='btn btn-sm btn-icon btn-light' onClick={() => setIsDrawerOpen(false)} style={{ borderRadius: '12px' }}>
                <i className='bi bi-x fs-1'></i>
              </button>
            </div>
          </div>

          <div className='card-body scroll-y px-10 pt-5 pb-10 hover-scroll-overlay-y h-400px' style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <form id='kt_call_result_form' onSubmit={handleSaveCallResult}>
              <div className='mb-5 p-3 rounded-4 border-0 position-relative overflow-hidden' 
                  style={{ background: 'var(--bs-light)', border: '1px solid var(--bs-gray-200)' }}>
                <div className='d-flex align-items-center justify-content-between'>
                    <div>
                        <h4 className='fw-bold text-gray-800 mb-1'>{selectedLead?.phone_number}</h4>
                        <span className='badge badge-success fw-bold animate__animated animate__flash animate__infinite'>
                            <span className='bullet bullet-dot bg-white me-2'></span> Active Call
                        </span>
                    </div>
                    <i className='bi bi-telephone-outbound fs-1 text-primary opacity-25'></i>
                </div>
              </div>

              <div className='row g-6'>
                {[
                    { label: 'Status Panggilan', val: valWelcomeStatusCall, set: setValWelcomeStatusCall, options: welcomeStatusCall },
                    { label: 'Hasil Panggilan', val: valWelcomeResultCall, set: setValWelcomeResultCall, options: welcomeResultCall },
                    { label: 'Status Utama', val: valWelcomeStatus, set: setValWelcomeStatus, options: welcomeStatus },
                    { label: 'Product Knowledge', val: valWelcomeProductKnowledge, set: setValWelcomeProductKnowledge, options: welcomeProductKnowledge }
                ].map((item, idx) => (
                    <div className='col-md-6 mb-2' key={idx}>
                        <label className='fs-9 fw-bold text-gray-700 mb-2 text-uppercase'>{item.label}</label>
                        <select className='form-select form-select-sm form-select-solid fw-bold border-0' 
                                style={{ background: 'var(--bs-gray-100)', borderRadius: '12px' }}
                                value={item.val} onChange={(e) => item.set(e.target.value)}>
                            <option value=''>Pilih...</option>
                            {item.options?.map((opt: any) => (<option key={opt.tablecode} value={opt.tablecode}>{opt.tablecode}</option>))}
                        </select>
                    </div>
                ))}
              </div>

              <div className='mt-4'>
                <label className='fs-9 fw-bold text-gray-700 mb-2 text-uppercase'>Telepon Terakhir</label>
                <input 
                  type='text' 
                  className='form-control form-control-sm form-control-solid fw-medium border-0' 
                  style={{ background: 'var(--bs-gray-200)', borderRadius: '15px', cursor: 'not-allowed' }}
                  readOnly
                  value={
                    selectedLead?.call_count === 0 ? 'Panggilan Pertama' :
                    selectedLead?.call_count === 1 ? 'Panggilan Kedua' :
                    selectedLead?.call_count === 2 ? 'Panggilan Ketiga' :
                    'Batas Maksimal Terlewati'
                  } 
                  placeholder="Tidak ada data panggilan" 
                />
              </div>

              <div className='mt-4'>
                <label className='fs-9 fw-bold text-gray-700 mb-2 text-uppercase'>No. Policy / No. Polis</label>
                <input type='text' className='form-control form-control-sm form-control-solid fw-medium border-0' 
                          style={{ background: 'var(--bs-gray-200)', borderRadius: '15px', cursor: 'not-allowed' }}
                          readOnly
                          value={policyNumber || ''} 
                          placeholder="Tidak ada nomor polis" />
              </div>

              <div className='mt-4'>
                <label className='fs-9 fw-bold text-gray-700 mb-2 text-uppercase'>Conversation Notes</label>
                <textarea className='form-control form-control-sm form-control-solid fw-medium border-0' 
                          style={{ background: 'var(--bs-gray-100)', borderRadius: '15px' }}
                          rows={5} value={callNote} onChange={(e) => setCallNote(e.target.value)} 
                          placeholder='Tulis ringkasan percakapan di sini...' />
              </div>
            </form>
          </div>

          <div className='card-footer py-8 px-10 d-flex flex-stack bg-body border-0'>
            <button type='button' className='btn btn-light fw-bold px-8' 
                    style={{ borderRadius: '12px' }} onClick={() => setIsDrawerOpen(false)}>
              Batal
            </button>
            <button type='submit' form='kt_call_result_form' className='btn btn-primary fw-bold px-10 shadow-sm' 
                    style={{ borderRadius: '12px' }} disabled={loading}>
              {!loading ? (
                <><i className='bi bi-check-lg me-2'></i> Simpan Hasil</>
              ) : (
                <span className='spinner-border spinner-border-sm ms-2'></span>
              )}
            </button>
          </div>
        </div>
      </div>
      {isDrawerOpen && <div className='position-fixed top-0 start-0 w-100 h-100 bg-black opacity-25 animate__animated animate__fadeIn' style={{ zIndex: 9998 }} onClick={() => setIsDrawerOpen(false)} />}
      
      <style>{`
        .sticky-column { 
          position: sticky !important; 
          z-index: 1; 
          background-color: inherit;
        }
        thead th.sticky-column { background-color: var(--bs-gray-100) !important; }
        tbody td.sticky-column { background-color: var(--bs-body-bg) !important; }
        .pagination .page-item.active .page-link { background-color: #6366f1 !important; color: white !important; }
        .scroll-y::-webkit-scrollbar { width: 5px; }
        .scroll-y::-webkit-scrollbar-thumb { background: var(--bs-gray-300); border-radius: 10px; }
        .nav-line-tabs .nav-item .nav-link.active { border-bottom: 3px solid #4f46e5 !important; color: #4f46e5 !important; }
      `}</style>
    </div>
  )
}